import { useRef, useState } from "react";
import { Paperclip, Sparkles, X } from "lucide-react";
import { Button, Card } from "@/components/ui-ext/primitives";
import {
  MAX_PROMPT_ATTACHMENTS,
  PROMPT_ATTACHMENT_ACCEPT,
  PromptAttachment,
  buildPromptAttachment,
  formatAttachmentSize,
  promptAttachmentError
} from "@/lib/prompt-attachments";

const EXAMPLE_BRIEFS = [
  "12-month strategic partnering journey for 40 HRBPs at an enterprise client — business acumen, consulting skills, credibility with the business. ~13 hours per learner, intermediate.",
  "Compact people-analytics upskilling journey for HR managers moving to data-driven decisions. About 10 hours, foundational to intermediate.",
  "Leadership-readiness journey for senior HR advisors preparing for HR director roles: strategy, change management, stakeholder influence. 20 hours over 6 months."
];

export function AdvisoryBriefComposer({
  busy,
  onGenerate
}: {
  busy: boolean;
  onGenerate: (input: {
    prompt: string;
    clientName?: string;
    attachments: PromptAttachment[];
  }) => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [clientName, setClientName] = useState("");
  const [attachments, setAttachments] = useState<PromptAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    setAttachmentError(null);
    for (const file of Array.from(files)) {
      const error = promptAttachmentError(file, attachments.length);
      if (error) {
        setAttachmentError(error);
        return;
      }
      const attachment = await buildPromptAttachment(file);
      setAttachments((current) => [...current, attachment]);
    }
  };

  const canGenerate = prompt.trim().length >= 10 && !busy;

  return (
    <Card accent className="p-5">
      <div className="mb-3">
        <h2 className="text-base font-bold text-anchor">New TLJ suggestion</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Describe the client's needs — themes, audience, seniority, hours per
          learner — or attach the client brief. Cosmos assembles a journey from
          real catalog content following the SME methodology.
        </p>
      </div>

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Client
      </label>
      <input
        value={clientName}
        onChange={(event) => setClientName(event.target.value)}
        placeholder="e.g. Stellenbosch University"
        className="mb-3 h-9 w-full rounded-[10px] border border-soft bg-surface px-3 text-sm text-anchor outline-none ring-focus placeholder:text-muted-foreground/70 focus:border-[var(--primary)]"
      />

      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Brief
      </label>
      <textarea
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        rows={5}
        placeholder="Development priorities, audience, time budget per learner, difficulty…"
        className="w-full resize-y rounded-[10px] border border-soft bg-surface p-3 text-sm text-anchor outline-none ring-focus placeholder:text-muted-foreground/70 focus:border-[var(--primary)]"
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={PROMPT_ATTACHMENT_ACCEPT}
          onChange={(event) => {
            void addFiles(event.target.files);
            event.target.value = "";
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={busy || attachments.length >= MAX_PROMPT_ATTACHMENTS}
          onClick={() => fileInputRef.current?.click()}
        >
          <Paperclip className="h-3.5 w-3.5" />
          Attach client brief
        </Button>
        {attachments.map((attachment) => (
          <span
            key={attachment.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-soft bg-muted/50 px-2.5 py-1 text-[11px] text-anchor"
          >
            {attachment.name}
            <span className="text-muted-foreground">
              {formatAttachmentSize(attachment.size)}
            </span>
            <button
              type="button"
              aria-label={`Remove ${attachment.name}`}
              onClick={() =>
                setAttachments((current) =>
                  current.filter((entry) => entry.id !== attachment.id)
                )
              }
              className="text-muted-foreground hover:text-[var(--danger)]"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      {attachmentError && (
        <p className="mt-1.5 text-xs text-[var(--danger)]">{attachmentError}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLE_BRIEFS.map((example) => (
            <button
              key={example}
              type="button"
              disabled={busy}
              onClick={() => setPrompt(example)}
              className="max-w-[260px] truncate rounded-full border border-soft bg-muted/40 px-2.5 py-1 text-left text-[11px] text-muted-foreground transition-colors hover:border-[var(--primary)] hover:text-anchor"
              title={example}
            >
              {example}
            </button>
          ))}
        </div>
        <Button
          disabled={!canGenerate}
          onClick={() =>
            onGenerate({
              prompt: prompt.trim(),
              clientName: clientName.trim() || undefined,
              attachments
            })
          }
        >
          <Sparkles className="h-4 w-4" />
          {busy ? "Generating…" : "Generate TLJ suggestion"}
        </Button>
      </div>
    </Card>
  );
}
